import { Input, Table, Tag } from "antd";
import useUsers from "hooks/useUsers";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { routesNames } from "utils/Constants";
import { getCurriculumPDF } from "services/Usuarios";
import useNotification from "hooks/useNotification";
import { FaFilePdf } from "react-icons/fa";
import { BiSearchAlt } from "react-icons/bi";
import { BASE_API } from "config/config";
import ExportExcel from "components/excel/ExportExcel";
import dayjs from "dayjs";
import { getAsociaciones } from 'services/Asociaciones';
// import { filter } from "lodash"; // No es necesario si se usa la búsqueda del backend

const InstitucionesTable = () => {
    const [value, setValue] = useState("");
    const [datasource, setDatasource] = useState([]);
    const { contextHolder, openNotification } = useNotification();
    // 'response' de useUsers ya no se usa para filtrar, solo para el estado inicial si es necesario
    const { isLoading, fetchData } = useUsers();
    const [dataExcel, setDataExcel] = useState([]);
    const [asociaciones, setAsociaciones] = useState([]);

    // 🆕 Estado para manejar la paginación del backend
    const [pagination, setPagination] = useState({
        current: 1, // Página actual
        pageSize: 20, // Elementos por página (coincide con el default del backend)
        total: 0, // Total de elementos (se actualizará con la respuesta de la API)
    });

    /**
     * 🆕 Función para obtener usuarios con paginación y búsqueda.
     * Se llama desde el useEffect, el Input de búsqueda y el onChange de la tabla.
     */
    const fetchUsers = async (page, pageSize, searchValue) => {
        // En tu hook useUsers, fetchData debe haber sido actualizado para aceptar 
        // page, pageSize, y searchValue (como 'query' o 'q') y pasarlos a getPaginacionUsuarios.

        // Parámetros que se enviarán a fetchData -> getPaginacionUsuarios
        // (null, true, "I", page, pageSize, searchValue)

        const responseData = await fetchData(null, true, "I", page, pageSize, searchValue);

        if (responseData && responseData.data && responseData.pagination) {
            const data = responseData.data;
            const apiPagination = responseData.pagination;

            setDatasource(data);
            dataExport(data); // Prepara datos para la exportación (solo los datos de la página actual)

            // Actualiza el estado de la paginación con los valores de la API
            setPagination({
                ...pagination,
                total: apiPagination.total,
                current: apiPagination.current_page,
                pageSize: apiPagination.per_page,
            });
        }
    };

    const fetchPdf = async (userId) => {
        const data = await getCurriculumPDF(userId);
        if (data?.uuid) {
            window.open(`${BASE_API}cv/${data.filename}`, "_blank", "noreferrer");
        } else {
            openNotification("El curriculum se encuentra incompleto", "success", "Atención!");
        }
    };

    async function obtenerAsociaciones() {
        const data = await getAsociaciones()
        setAsociaciones(data)
    }

    // Llama a fetchUsers y obtenerAsociaciones solo en la primera carga.
    useEffect(() => {
        fetchUsers(pagination.current, pagination.pageSize, value);
        obtenerAsociaciones();
    }, []); // Dependencias vacías para cargar solo una vez.

    const dataExport = (data) => {
        const exportData = data.map((item) => {
            return {
                Perfil: item.type === 'I' ? 'Institución' : item.type === 'P' ? 'Postulante' : 'Administrador',
                Nombre: item.name,
                Email: item.email,
                Creado: dayjs(item.created_at).format('DD/MM/YYYY'),
                Plan: item.plan?.detalle?.nombre,
                // Uso de `item.cuenta?.asociacion_id` para asegurar que sea el ID correcto
                Asociación: asociaciones.filter(asociacion => asociacion.id === item.cuenta?.asociacion_id)[0]?.nombre || '',
                Estado: item.cuenta?.rechazado ? "Rechazado" : item.cuenta?.suspendido ? "Suspendido" : item.cuenta?.activo ? "Activo" : "Inactivo",
            }
        })

        setDataExcel(exportData)
    }

    const columns = [
        // Aquí están todas las columnas... (sin cambios)
        {
            title: "Perfil",
            dataIndex: "type",
            key: "type",
            filters: [
                { text: "Institución", value: "I" },
                { text: "Postulante", value: "P" },
                { text: "Administrador", value: "A" },
            ],
            // ⚠️ Nota: Los filtros de Ant Design son para el frontend. Si usas paginación del backend, 
            // no funcionarán correctamente a menos que se implemente la lógica de filtro 
            // de la tabla para enviar un parámetro adicional al backend. 
            // Por ahora, solo filtran los datos *actualmente visibles*.
            onFilter: (value, record) => record.type === value,
            render: (text) => {
                if (text === "I") return <Tag color="lime">Institución</Tag>;
                if (text === "P") return <Tag color="blue">Postulante</Tag>;
                return <Tag color="orange">Administrador</Tag>;
            },
        },
        {
            title: "Nombre",
            dataIndex: "nombre",
            key: "nombre",
            render: (text, record) => (record?.name),
        },
        {
            title: "Email",
            dataIndex: "email",
        },
        {
            title: "Creado",
            dataIndex: "created_at",
            render: (text) => new Date(text).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: "Última sesión",
            dataIndex: "last_login_at",
            render: (text) => new Date(text).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }),
            sorter: (a, b) => new Date(a.last_login_at) - new Date(b.last_login_at),
        },
        {
            title: "Plan",
            dataIndex: "plan",
            render: (text, record) => {
                if (record?.type === "P" || record?.type === "A") return null;
                if (!record?.plan) return <Tag color="purple">STANDARD</Tag>;
                return (
                    <Tag color={record?.plan.plan_id === 1 ? "gold" : "purple"}>
                        {record.plan.detalle.nombre.toUpperCase()}
                    </Tag>
                );
            },
            filters: [
                { text: "STANDARD", value: "Standard" },
                { text: "GOLD", value: "Gold" },
            ],
            onFilter: (value, record) => record.plan?.detalle?.nombre === value,
        },
        {
            title: "Verificado",
            dataIndex: "verificado",
            render: (text, record) => (
                <Tag color={record.cuenta?.verificado === 0 ? "red" : "green"}>
                    {record.cuenta?.verificado === 0 ? "Pendiente" : "Verificado"}
                </Tag>
            ),
            filters: [
                { text: "Pendiente", value: 0 },
                { text: "Verificado", value: 1 },
            ],
            onFilter: (value, record) => record.cuenta?.verificado === value,
        },
        {
            title: "Asociación",
            dataIndex: "asociacion_id",
            render: (text, record) => (asociaciones.filter(asociacion => asociacion.id === record.cuenta?.asociacion_id)[0]?.nombre),
            filters: asociaciones.map((asociacion) => ({ text: asociacion.nombre, value: asociacion.id })),
            onFilter: (value, record) => record.cuenta?.asociacion_id === value,
        },
        {
            title: "Estado",
            dataIndex: "estado",
            render: (text, record) => {
                if (record.cuenta?.rechazado) return <Tag color="red">Rechazado</Tag>;
                if (record.cuenta?.suspendido) return <Tag color="red">Suspendido</Tag>;
                return <Tag color={record.cuenta?.activo ? "green" : "red"}>{record.cuenta?.activo ? "Activo" : "Inactivo"}</Tag>;
            },
        },
        {
            title: "Acciones",
            dataIndex: "detalle",
            render: (text, record) => (
                <div className="flex items-center gap-1">
                    <Link to={`${routesNames.USER}/${record.id}`} className="bg-main text-sm px-4 py-1 rounded-lg">
                        Ver
                    </Link>
                    {record?.type === "P" && (
                        record?.cuenta?.fecha_perfil_completo !== null &&
                        <a onClick={() => fetchPdf(record?.id)} className="bg-red-500 text-xs text-white px-2 rounded-md py-1 flex items-center">
                            <FaFilePdf className="text-white" /> Descargar
                        </a>
                    )}
                </div>
            ),
        },
    ];


    return (
        <>
            {contextHolder}
            <Input
                allowClear
                placeholder="Buscar por Nombre, Email, Plan o Tipo de usuario"
                prefix={<BiSearchAlt className="text-xl" />}
                value={value}
                className="p-3 w-full rounded-lg mb-2"
                onChange={(e) => {
                    const currValue = e.target.value;
                    setValue(currValue);

                    // 🆕 Dispara la búsqueda al backend
                    // Siempre vuelve a la página 1 al buscar
                    fetchUsers(1, pagination.pageSize, currValue);

                    // Actualiza la paginación localmente para reflejar el inicio en la página 1
                    setPagination(prev => ({ ...prev, current: 1 }));
                }}
            />

            <div className="flex flex-row justify-between items-center mb-3">
                <span className="block text-sm font-semibold text-start my-1 me-5">
                    {pagination.total} usuarios encontrados
                </span>
                <ExportExcel data={dataExcel} fileName={"Usuarios_" + new Date().toLocaleDateString()} />
            </div>


            <Table
                loading={isLoading}
                dataSource={datasource}
                columns={columns}
                className="w-full"
                rowKey={(record) => record.id}
                size="small"
                // Configuración del objeto pagination
                pagination={{
                    // 🚩 CLAVE 1: Pasa todos los datos de paginación controlados por el estado
                    total: pagination.total,
                    current: pagination.current,
                    pageSize: pagination.pageSize,

                    // 🚩 CLAVE 2: Asegúrate de que el selector de tamaño esté DESHABILITADO
                    showSizeChanger: false,

                    // Opcional pero recomendado para un look limpio: Ocultar el salto rápido
                    showQuickJumper: false,

                    // Opcional: Puedes seguir mostrando el total de registros encontrados
                    showTotal: (total, range) => `Mostrando ${range[0]}-${range[1]} de ${total} usuarios`,

                    // ⚠️ Asegúrate de NO incluir 'pageSizeOptions' si showSizeChanger es false.
                }}
                locale={{ emptyText: "No hay resultados" }}
                onChange={(newPagination, filters, sorter, extra) => {
                    // Lógica para el cambio de página:
                    if (newPagination.current !== pagination.current) {
                        fetchUsers(newPagination.current, pagination.pageSize, value);
                    }
                    dataExport(extra.currentDataSource)
                }}
            />

        </>
    );
};

export default InstitucionesTable;